import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  RenderDetail,
  RegenLimitError,
  approveRender,
  fetchRender,
  regenerateSegment,
  retryRender,
} from '../../api/rendersApi';
import { approveSoundbedAssignment } from '../../api/soundbedAdminApi';
import { fetchStory } from '../../api/storiesApi';
import { ApiError, isAbortError, toApiError } from '../../api/http';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { Story, useApp } from '../../context/AppContext';
import StoryArtwork from '../../components/StoryArtwork/StoryArtwork';
import { EmptyState, Panel, PillButton, Player, StateMessage } from '../../components/ui';
import { CheckIcon, PlayIcon } from '../../components/ui/uiIcons';
import { RefreshIcon } from '../../components/icons';
import { formatDurationLabel } from '../../utils/duration';
import {
  addReviewSampleProgress,
  approvalRuntimeSeconds,
  canApproveAfterListen,
  canApproveAfterReviewSamples,
  completedReviewSampleIds,
  introCheckpointSeconds,
  listenedRatio,
} from './approvalListen';
import './Renders.css';

type ReviewMix = 'soundbed' | 'voice_only';
const VOICE_ONLY_COMPARE_SECONDS = 5;
// Display threshold for the operator's mixed listen check; the approval gate
// itself is decided by canApproveAfterListen, unchanged.
const OPERATOR_COVERAGE_PERCENT = 80;

const RenderReview: React.FC = () => {
  const { renderId } = useParams<{ renderId: string }>();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastPlaybackPositionRef = useRef<number | null>(null);
  const seekingRef = useRef(false);
  const playbackSourceRef = useRef<string | null>(null);
  const bedtime = useBedtimeContext();
  const { state: appState } = useApp();

  const [render, setRender] = useState<RenderDetail | null>(null);
  const [pairMismatch, setPairMismatch] = useState<RenderDetail | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentS, setCurrentS] = useState(0);
  const [durationS, setDurationS] = useState(0);
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);
  const [regenMessage, setRegenMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [failedAction, setFailedAction] = useState<'regenerate' | 'approve' | 'soundbed' | 'retry' | null>(null);
  const [failedSegmentId, setFailedSegmentId] = useState<number | null>(null);
  const [regenLimitHit, setRegenLimitHit] = useState(false);
  const [approving, setApproving] = useState(false);
  const [listenedSeconds, setListenedSeconds] = useState(0);
  const [reviewSampleSeconds, setReviewSampleSeconds] = useState<Record<string, number>>({});
  const [introListened, setIntroListened] = useState(false);
  const [goodnightListened, setGoodnightListened] = useState(false);
  const [voiceOnlyListenedSeconds, setVoiceOnlyListenedSeconds] = useState(0);
  const [bookendsForegrounded, setBookendsForegrounded] = useState(false);
  const [endedInSilence, setEndedInSilence] = useState(false);
  const [operatorReviewing, setOperatorReviewing] = useState(false);
  const [reviewMix, setReviewMix] = useState<ReviewMix>('soundbed');
  const [retrying, setRetrying] = useState(false);
  // Media identity fallback for direct entry: the story record fetched once
  // when the loaded catalog does not hold this render's story.
  const [fetchedStory, setFetchedStory] = useState<Story | null>(null);
  const identityFetchRef = useRef<{ storyId: string; controller: AbortController } | null>(null);
  const renderStatus = render?.status;
  const renderStoryId = render?.story_id;
  const catalogHasStory = Boolean(
    renderStoryId && appState.stories.some((item) => item.story_id === renderStoryId)
  );
  const reviewSamples = useMemo(
    () => render?.review_samples || [],
    [render?.review_samples]
  );
  const completedSampleIds = useMemo(
    () => completedReviewSampleIds(reviewSamples, reviewSampleSeconds),
    [reviewSamples, reviewSampleSeconds]
  );

  useEffect(() => {
    if (!renderId) return;
    const controller = new AbortController();
    setRender(null);
    setPairMismatch(null);
    setLoadError(null);
    fetchRender(renderId, false, controller.signal)
      .then((r) => {
        if (bedtime.pair && (r.household_id !== bedtime.pair.householdId || r.listener_id !== bedtime.pair.listenerId || r.voice_id !== bedtime.pair.voiceId)) {
          setPairMismatch(r);
          return;
        }
        setRender(r);
        setRegenLimitHit(r.regens_today >= r.regen_limit);
        setListenedSeconds(0);
        setReviewSampleSeconds({});
        setIntroListened(false);
        setGoodnightListened(false);
        setVoiceOnlyListenedSeconds(0);
        setBookendsForegrounded(false);
        setEndedInSilence(false);
        setReviewMix('soundbed');
        lastPlaybackPositionRef.current = null;
        playbackSourceRef.current = null;
      })
      .catch((error) => {
        if (!isAbortError(error)) setLoadError(toApiError(error));
      });
    return () => controller.abort();
  }, [bedtime.pair, renderId]);

  useEffect(() => {
    if (!renderId || !renderStatus || !['queued', 'rendering'].includes(renderStatus)) {
      return;
    }
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      fetchRender(renderId, false, controller.signal)
        .then((next) => { if (!controller.signal.aborted) setRender(next); })
        .catch((error) => { if (!isAbortError(error) && !controller.signal.aborted) setLoadError(toApiError(error)); });
    }, 8000);
    return () => { window.clearInterval(timer); controller.abort(); };
  }, [renderId, renderStatus]);

  // Direct entry to a review (a link, a refresh) can arrive before the catalog
  // has loaded. Fetch the story once with the existing GET /stories/{story_id}
  // so the identity shows its artwork exactly as entry from Tonight does; the
  // title alone stands in while it loads or if the fetch fails.
  useEffect(() => () => identityFetchRef.current?.controller.abort(), []);
  useEffect(() => {
    if (!renderStoryId || catalogHasStory) return;
    if (identityFetchRef.current?.storyId === renderStoryId) return;
    identityFetchRef.current?.controller.abort();
    const controller = new AbortController();
    identityFetchRef.current = { storyId: renderStoryId, controller };
    fetchStory(renderStoryId, controller.signal)
      .then((story) => {
        if (!controller.signal.aborted && story && story.story_id === renderStoryId) {
          setFetchedStory(story);
        }
      })
      .catch(() => {
        // Title only. Artwork is decorative and never blocks the review.
      });
  }, [renderStoryId, catalogHasStory]);

  const playAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().catch(() => setIsPlaying(false));
  }, []);

  const pauseAudio = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const seekAudio = useCallback((seconds: number) => {
    // The scrubber only moves the position. Credit comes from the accumulator's
    // timeupdate deltas, and a seek is excluded there.
    if (audioRef.current) audioRef.current.currentTime = seconds;
    setCurrentS(seconds);
  }, []);

  const seekTo = useCallback((ms: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = ms / 1000;
    audio.play().catch(() => setIsPlaying(false));
  }, []);

  const selectReviewMix = useCallback((nextMix: ReviewMix) => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setReviewMix(nextMix);
    setIsPlaying(false);
    setCurrentS(0);
    setDurationS(0);
    lastPlaybackPositionRef.current = null;
    playbackSourceRef.current = null;
  }, []);

  const recordPlaybackProgress = useCallback((
    audio: HTMLAudioElement,
    includeEndedInterval = false,
  ) => {
    // Only an active playback session for this source can accrue credit.
    // In particular, paused/seek events cannot create a new session.
    if (playbackSourceRef.current !== audio.src) return;
    const current = audio.currentTime;
    const previous = lastPlaybackPositionRef.current;
    if (
      !seekingRef.current
      && !audio.seeking
      && (!audio.paused || includeEndedInterval)
      && previous !== null
    ) {
      const delta = current - previous;
      // Normal media timeupdates remain small even at accelerated review speed.
      // A larger jump is a seek and does not count as listening.
      if (reviewMix === 'soundbed' && delta > 0 && delta <= 5) {
        setListenedSeconds((seconds) => seconds + delta);
        setReviewSampleSeconds((progress) => addReviewSampleProgress(
          reviewSamples,
          progress,
          previous,
          current
        ));
      } else if (reviewMix === 'voice_only' && delta > 0 && delta <= 5) {
        setVoiceOnlyListenedSeconds((seconds) => seconds + delta);
      }
    }
    lastPlaybackPositionRef.current = current;
  }, [reviewMix, reviewSamples]);

  const handleRegenerate = useCallback(
    async (segmentId: number) => {
      if (!renderId || regeneratingId !== null) return;
      setRegeneratingId(segmentId);
      setRegenMessage(null);
      setActionError(null);
      setFailedAction(null);
      setFailedSegmentId(null);
      try {
        const result = await regenerateSegment(renderId, segmentId);
        setRender((prev) =>
          prev
            ? {
                ...prev,
                status: result.status,
                audio_url: result.audio_url,
                speech_only_audio_url: result.speech_only_audio_url,
                // The response returns the manifest without merged text.
                // Keep the text we already have and update timings by id.
                segments: prev.segments.map((s) => {
                  const updated = result.segments.find(
                    (n) => n.segment_id === s.segment_id
                  );
                  return updated ? { ...s, ...updated, text: s.text } : s;
                }),
                regens_today: result.regens_today,
              }
            : prev
        );
        setRegenLimitHit(result.regens_today >= result.regen_limit);
        setListenedSeconds(0);
        setReviewSampleSeconds({});
        setIntroListened(false);
        setGoodnightListened(false);
        lastPlaybackPositionRef.current = null;
        playbackSourceRef.current = null;
        setRegenMessage('That part is ready. Have another listen.');
      } catch (e) {
        if (e instanceof RegenLimitError) {
          setRegenLimitHit(true);
          setRegenMessage(
            "You've used today's retries for this story. They refresh tomorrow."
          );
        } else {
          setActionError(toApiError(e, 'The segment could not be prepared.'));
          setFailedAction('regenerate');
          setFailedSegmentId(segmentId);
        }
      } finally {
        setRegeneratingId(null);
      }
    },
    [renderId, regeneratingId]
  );

  const handleApprove = useCallback(async () => {
    if (!renderId || !render || render.status !== 'machine_passed' || reviewMix !== 'soundbed') return;
    const runtimeSeconds = approvalRuntimeSeconds(
      render.output_seconds,
      render.segments
    );
    if (!canApproveAfterReviewSamples(reviewSamples, completedSampleIds)) return;
    const introReviewed = !reviewSamples.some((sample) => sample.sample_id === 'intro')
      || completedSampleIds.includes('intro');
    const goodnightReviewed = !reviewSamples.some((sample) => sample.sample_id === 'goodnight')
      || completedSampleIds.includes('goodnight');
    setApproving(true);
    setActionError(null);
    setFailedAction(null);
    try {
      const result = await approveRender(renderId, {
        evidence_version: 2,
        listened_seconds: listenedSeconds,
        completion_ratio: listenedRatio(listenedSeconds, runtimeSeconds),
        intro_listened: introReviewed,
        goodnight_listened: goodnightReviewed,
        reviewed_sample_ids: completedSampleIds,
      });
      const matchesCurrentPair = Boolean(
        bedtime.pair
        && render.household_id === bedtime.pair.householdId
        && render.listener_id === bedtime.pair.listenerId
        && render.voice_id === bedtime.pair.voiceId
      );
      if (matchesCurrentPair) {
        navigate('/my-stories', { replace: true });
      } else {
        setRender({ ...render, status: result.status });
      }
    } catch (e) {
      setActionError(toApiError(e, 'This story could not be approved.'));
      setFailedAction('approve');
    } finally {
      setApproving(false);
    }
  }, [
    renderId,
    render,
    reviewMix,
    listenedSeconds,
    reviewSamples,
    completedSampleIds,
    bedtime.pair,
    navigate,
  ]);

  const handleApproveSoundbedAssignment = useCallback(async () => {
    if (
      !renderId
      || !render?.soundbed_review_assignment_id
      || render.soundbed_assignment_state !== 'listening_review'
    ) return;
    const runtimeSeconds = approvalRuntimeSeconds(
      render.output_seconds,
      render.segments,
    );
    const voiceOnlyCompared = voiceOnlyListenedSeconds >= VOICE_ONLY_COMPARE_SECONDS;
    if (
      !canApproveAfterListen(
        { listenedSeconds, introListened, goodnightListened },
        runtimeSeconds,
      )
      || !voiceOnlyCompared
      || !bookendsForegrounded
      || !endedInSilence
    ) return;
    setOperatorReviewing(true);
    setRegenMessage(null);
    setActionError(null);
    setFailedAction(null);
    try {
      await approveSoundbedAssignment(render.soundbed_review_assignment_id, {
        render_id: renderId,
        listened_seconds: listenedSeconds,
        completion_ratio: listenedRatio(listenedSeconds, runtimeSeconds),
        intro_listened: introListened,
        goodnight_listened: goodnightListened,
        voice_only_compared: voiceOnlyCompared,
        bookends_foregrounded: bookendsForegrounded,
        ended_in_silence: endedInSilence,
      });
      setRender((current) => current
        ? { ...current, soundbed_assignment_state: 'approved' }
        : current);
      selectReviewMix('soundbed');
      setRegenMessage(
        'Internal soundbed review approved. You can now approve this story for bedtime.',
      );
    } catch (e) {
      setActionError(toApiError(e, 'The soundbed review could not be saved.'));
      setFailedAction('soundbed');
    } finally {
      setOperatorReviewing(false);
    }
  }, [
    renderId,
    render,
    listenedSeconds,
    introListened,
    goodnightListened,
    voiceOnlyListenedSeconds,
    bookendsForegrounded,
    endedInSilence,
    selectReviewMix,
  ]);

  const handleRetry = useCallback(async () => {
    if (!renderId || retrying) return;
    setRetrying(true);
    setRegenMessage(null);
    setActionError(null);
    setFailedAction(null);
    try {
      const result = await retryRender(renderId);
      if (result.render_id) {
        navigate(`/renders/${result.render_id}`, { replace: true });
      } else {
        navigate('/renders');
      }
    } catch (error) {
      setActionError(toApiError(error, 'This story could not be retried.'));
      setFailedAction('retry');
    } finally {
      setRetrying(false);
    }
  }, [navigate, renderId, retrying]);

  const retryFailedAction = useCallback(() => {
    if (failedAction === 'approve') void handleApprove();
    else if (failedAction === 'soundbed') void handleApproveSoundbedAssignment();
    else if (failedAction === 'retry') void handleRetry();
    else if (failedAction === 'regenerate' && failedSegmentId !== null) {
      void handleRegenerate(failedSegmentId);
    }
  }, [failedAction, failedSegmentId, handleApprove, handleApproveSoundbedAssignment, handleRegenerate, handleRetry]);

  const backLink = (
    <div className="review__back">
      <PillButton variant="secondary" size="compact" to="/renders">← Your stories</PillButton>
    </div>
  );

  if (loadError) {
    return (
      <div className="review">
        <StateMessage kind="error" error={loadError} title="This story is not available here" headingLevel={1} retry={{ label: 'Back to Tonight', to: '/renders' }} />
        <Link to="/renders" className="review__text-link">
          ← Back to your stories
        </Link>
      </div>
    );
  }

  if (pairMismatch) {
    const reviewVoiceName = bedtime.voices.find((voice) => voice.voice_id === pairMismatch.voice_id)?.voice_name || 'a Family voice';
    const reviewListenerName = pairMismatch.listener_name || 'the selected listener';
    const currentListenerName = bedtime.listeners.find((listener) => listener.listener_id === bedtime.pair?.listenerId)?.display_name || 'your listener';
    const currentVoiceName = bedtime.voices.find((voice) => voice.voice_id === bedtime.pair?.voiceId)?.voice_name || 'your Family voice';
    const canUsePair = Boolean(pairMismatch.household_id && pairMismatch.listener_id && pairMismatch.voice_id);
    return (
      <div className="review">
        <EmptyState
          headingLevel={1}
          title={`This review is for ${reviewListenerName} with ${reviewVoiceName}.`}
          message={`You’ve selected ${currentListenerName} with ${currentVoiceName}.`}
          action={canUsePair ? {
            label: 'Use this listener and voice',
            onClick: () => bedtime.openPairDialog({
              householdId: pairMismatch.household_id || undefined,
              listenerId: pairMismatch.listener_id || undefined,
              voiceId: pairMismatch.voice_id,
            }),
          } : undefined}
          testId="review-pair-mismatch"
        />
        <div className="review__back">
          <PillButton variant="secondary" to="/renders">Back to Tonight</PillButton>
        </div>
      </div>
    );
  }

  if (!render) {
    return (
      <div className="review">
        <StateMessage kind="loading" title="Loading your story…" headingLevel={1} testId="review-loading" />
      </div>
    );
  }

  // Media identity uses the catalog's story record when it is loaded, else the
  // record fetched once for direct entry; until then the identity is the title.
  const story = appState.stories.find((item) => item.story_id === render.story_id)
    ?? (fetchedStory?.story_id === render.story_id ? fetchedStory : undefined);
  const identityArt = story ? (
    <span className="review__art" aria-hidden="true">
      <StoryArtwork src={story.thumbnail_url} srcSet={story.thumbnail_srcset} sizes="120px" imageClassName="review__image" fallbackClassName="review__fallback" fallbackSize={28} />
    </span>
  ) : null;

  if (render.status === 'failed') {
    return (
      <div className="review">
        {backLink}
        <header className="review__identity">
          {identityArt}
          <div className="review__identity-text">
            <h1>{render.story_title}</h1>
            {render.listener_name && <p className="review__meta">for {render.listener_name}</p>}
          </div>
        </header>
        <StateMessage
          kind="error"
          title="This story wasn’t prepared"
          message="Your Family voice and story are safe. Luma stopped this attempt so it would not stay stuck."
          retry={{ label: 'Retry this story', busyLabel: 'Starting retry…', busy: retrying, onClick: () => void handleRetry() }}
          testId="review-failed"
        />
        {actionError && <StateMessage kind="error" urgent error={actionError} title="We couldn’t restart this story" />}
        {regenMessage && <p className="review__message" role="status">{regenMessage}</p>}
      </div>
    );
  }

  if (render.status === 'queued' || render.status === 'rendering') {
    return (
      <div className="review">
        {backLink}
        <header className="review__identity">
          {identityArt}
          <div className="review__identity-text">
            <h1>
              {render.status === 'queued'
                ? 'Your story is waiting its turn'
                : 'Your story is being prepared'}
            </h1>
            <p className="review__status-line">{render.story_title}</p>
          </div>
        </header>
        <StateMessage kind="loading" title="Preparing your story…" testId="review-preparing" />
        <p className="review__note">This page will update automatically. You can safely come back later.</p>
      </div>
    );
  }

  const approved = render.status === 'parent_approved';
  const runtimeSeconds = approvalRuntimeSeconds(render.output_seconds, render.segments);
  const introCheckpoint = introCheckpointSeconds(runtimeSeconds, render.segments);
  const listenState = { listenedSeconds, introListened, goodnightListened };
  const approvalReady = reviewMix === 'soundbed'
    && render.status === 'machine_passed'
    && canApproveAfterReviewSamples(reviewSamples, completedSampleIds);
  const operatorAssignmentPending = Boolean(
    render.soundbed_review_assignment_id
    && render.soundbed_assignment_state === 'listening_review'
  );
  const voiceOnlyCompared = voiceOnlyListenedSeconds >= VOICE_ONLY_COMPARE_SECONDS;
  const operatorReviewReady = operatorAssignmentPending
    && canApproveAfterListen(listenState, runtimeSeconds)
    && voiceOnlyCompared
    && bookendsForegrounded
    && endedInSilence;
  const coveragePercent = Math.round(listenedRatio(listenedSeconds, runtimeSeconds) * 100);
  // Presentation only: the three operator checks in the checkpoint list's
  // shape. Readiness is still decided by operatorReviewReady above.
  const operatorChecks = [
    {
      id: 'coverage',
      label: 'Mixed story listen',
      complete: coveragePercent >= OPERATOR_COVERAGE_PERCENT,
      remaining: `${Math.max(0, OPERATOR_COVERAGE_PERCENT - coveragePercent)}% left`,
    },
    {
      id: 'bookends',
      label: 'Personal introduction and goodnight',
      complete: introListened && goodnightListened,
      remaining: 'not yet',
    },
    {
      id: 'voice-only',
      label: 'Voice-only comparison',
      complete: voiceOnlyCompared,
      remaining: `${Math.max(0, VOICE_ONLY_COMPARE_SECONDS - Math.floor(voiceOnlyListenedSeconds))} sec left`,
    },
  ];
  const activeSegmentId = render.segments.find(
    (s) => currentS * 1000 >= s.start_ms && currentS * 1000 < s.end_ms
  )?.segment_id;
  const canCompareMix = Boolean(
    render.has_soundbed && render.speech_only_audio_url && render.audio_url
  );
  const activeAudioUrl = reviewMix === 'voice_only'
    ? render.speech_only_audio_url
    : render.audio_url;
  const voiceName = bedtime.voices.find((voice) => voice.voice_id === render.voice_id)?.voice_name;
  const durationLabel = formatDurationLabel(render.estimated_duration_minutes, render.output_seconds);
  const approveLabel = approving
    ? 'Approving…'
    : operatorAssignmentPending
    ? 'Complete internal soundbed review first'
    : render.status === 'needs_review'
    ? 'Waiting for Luma to finish checking'
    : approvalReady
    ? 'Approve for bedtime'
    : 'Review each moment to approve';
  // The white pill appears only when the existing approve gate allows it.
  const approvePrimary = approvalReady && !operatorAssignmentPending;

  return (
    <div className="review">
      {backLink}
      <header className="review__identity">
        {identityArt}
        <div className="review__identity-text">
          <h1>{render.story_title}</h1>
          <p className="review__status-line">
            {approved
              ? 'All set for tonight'
              : render.status === 'needs_review'
              ? 'Luma is checking this story'
              : 'Your story is ready for a listen'}
          </p>
          <p className="review__meta">
            {durationLabel}
            {render.listener_name
              ? ` · for ${render.listener_name}`
              : ''}
          </p>
          {render.content_scope && <p className="review__meta">{render.content_scope.label}</p>}
        </div>
      </header>

      {render.status === 'needs_review' && (
        <p className="review__notice" data-testid="needs-review-notice">
          We're taking another look at a couple of moments in this story.
          You can listen or prepare a segment again while Luma finishes this
          safety check. Approval will become available when it advances.
        </p>
      )}

      <section className="review__player" aria-label="Story playback" data-testid="player-toggle">
        <audio
          ref={audioRef}
          src={activeAudioUrl ?? undefined}
          preload="metadata"
          onPlay={(event) => {
            setIsPlaying(true);
            playbackSourceRef.current = event.currentTarget.src;
            lastPlaybackPositionRef.current = event.currentTarget.currentTime;
          }}
          onPause={(event) => {
            // Natural end can dispatch pause before ended. Finalize before
            // clearing the session; ordinary pauses never gain terminal credit.
            if (event.currentTarget.ended) recordPlaybackProgress(event.currentTarget, true);
            setIsPlaying(false);
            lastPlaybackPositionRef.current = null;
            playbackSourceRef.current = null;
          }}
          onEnded={(event) => {
            // Browsers report ended media as paused. Count the final natural
            // playback interval so the closing checkpoint is not lost.
            recordPlaybackProgress(event.currentTarget, true);
            setIsPlaying(false);
            if (reviewMix === 'soundbed') setGoodnightListened(true);
            lastPlaybackPositionRef.current = null;
            playbackSourceRef.current = null;
          }}
          onTimeUpdate={(event) => {
            const audio = event.currentTarget;
            setCurrentS(audio.currentTime);
            // At natural end, timeupdate already reports paused. Consume the
            // last interval here before advancing the baseline; later terminal
            // events see a zero delta or a cleared session, never double credit.
            recordPlaybackProgress(audio, audio.ended);
            if (reviewMix === 'soundbed') {
              const intro = reviewSamples.find((sample) => sample.sample_id === 'intro');
              if (audio.currentTime >= (intro?.end_seconds ?? introCheckpoint)) {
                setIntroListened(true);
              }
            }
          }}
          onSeeking={() => { seekingRef.current = true; }}
          onSeeked={(event) => {
            lastPlaybackPositionRef.current = playbackSourceRef.current === event.currentTarget.src
              ? event.currentTarget.currentTime : null;
            seekingRef.current = false;
          }}
          onLoadedMetadata={(e) => setDurationS(e.currentTarget.duration)}
        />
        <Player
          mode="review"
          title={render.story_title}
          subtitle={canCompareMix ? (reviewMix === 'soundbed' ? 'Gentle music' : 'Voice only') : voiceName}
          artwork={story ? { src: story.thumbnail_url, srcSet: story.thumbnail_srcset } : null}
          playing={isPlaying}
          currentTime={currentS}
          duration={durationS || render.output_seconds || 0}
          onPlay={playAudio}
          onPause={pauseAudio}
          onSeek={seekAudio}
        />
      </section>

      {canCompareMix && (
        <fieldset className="review__mix" data-testid="mix-comparison">
          <legend>Compare the final mix</legend>
          <p>
            Switch between versions at the same mastered length. Listen to
            the gentle-music version before approving it for bedtime.
          </p>
          <div className="review__mix-choices">
            <label>
              <input
                type="radio"
                name="review-mix"
                value="soundbed"
                checked={reviewMix === 'soundbed'}
                onChange={() => selectReviewMix('soundbed')}
              />
              Gentle music
            </label>
            <label>
              <input
                type="radio"
                name="review-mix"
                value="voice_only"
                checked={reviewMix === 'voice_only'}
                onChange={() => selectReviewMix('voice_only')}
              />
              Voice only
            </label>
          </div>
          <p className="review__mix-status" aria-live="polite">
            Reviewing {reviewMix === 'soundbed' ? 'gentle music' : 'voice only'}.
          </p>
          {render.soundbed_attribution && (
            <p className="review__mix-credit">
              Soundbed credit: {render.soundbed_attribution}
            </p>
          )}
        </fieldset>
      )}

      {actionError && <StateMessage kind="error" urgent error={actionError} title="That action did not finish" retry={{ label: 'Try again', onClick: retryFailedAction, busy: approving || operatorReviewing || retrying }} />}
      {regenMessage && <p className="review__message" role="status">{regenMessage}</p>}

      {approved ? (
        <p className="review__approved" data-testid="approved-confirmation">
          ✓ Approved. {render.listener_name || 'The selected listener'} can listen tonight.
        </p>
      ) : (
        <section className="review__gate" aria-labelledby="approval-gate-heading">
          <h2 id="approval-gate-heading">Listen before approving</h2>
          <p className="review__gate-help">
            Hear the personal moments plus short samples from the beginning,
            middle, and end. Tap each moment and listen until it is checked.
          </p>
          <div className="review__samples" role="group" aria-label="Review checkpoints" aria-live="polite">
            {reviewSamples.map((sample) => {
              const complete = completedSampleIds.includes(sample.sample_id);
              const heard = Math.min(
                sample.required_seconds,
                reviewSampleSeconds[sample.sample_id] || 0
              );
              return <button
                type="button"
                key={sample.sample_id}
                className={`review__sample${complete ? ' complete' : ''}`}
                onClick={() => {
                  const audio = audioRef.current;
                  if (!audio) return;
                  audio.currentTime = sample.start_seconds;
                  lastPlaybackPositionRef.current = sample.start_seconds;
                  void audio.play();
                }}
              >
                <span className="review__check-label">
                  {complete ? <CheckIcon size={18} /> : <PlayIcon size={18} />}
                  {sample.label}
                </span>
                <small>{complete ? 'heard' : `${Math.ceil(Math.max(0, sample.required_seconds - heard))} sec left`}</small>
              </button>;
            })}
          </div>
          {reviewSamples.length === 0 && (
            <p className="review__samples-error">
              Review checkpoints are unavailable. Refresh before approving.
            </p>
          )}
          <div className="review__approve">
            <PillButton
              variant={approvePrimary ? 'primary' : 'secondary'}
              onClick={() => void handleApprove()}
              disabled={
                approving
                || !approvalReady
                || operatorAssignmentPending
              }
              data-testid="approve-button"
            >
              {approveLabel}
            </PillButton>
          </div>
        </section>
      )}

      <section className="review__segments" aria-label="Story segments">
        <h2>Listen by part</h2>
        <p className="review__segments-help">
          You can remake one part without changing the rest of the story.
        </p>
        <ol className="review__segment-list" data-testid="segment-list">
          {render.segments.map((seg) => (
            <li
              key={seg.segment_id}
              className={`review__segment${
                seg.segment_id === activeSegmentId ? ' review__segment--active' : ''
              }`}
            >
              <button
                type="button"
                className="review__segment-play"
                onClick={() => seekTo(seg.start_ms)}
                aria-label={`Play segment ${seg.segment_id}`}
              >
                <PlayIcon size={20} />
              </button>
              <span className="review__segment-text">{seg.text || `Part ${seg.segment_id}`}</span>
              <button
                type="button"
                className="review__segment-regen"
                onClick={() => handleRegenerate(seg.segment_id)}
                disabled={regeneratingId !== null || regenLimitHit || approved}
                aria-label={`Make part ${seg.segment_id} again`}
                title={
                  regenLimitHit
                    ? "Today's retries are used up"
                    : 'Make this part again'
                }
              >
                {regeneratingId === seg.segment_id ? (
                  <span className="review__segment-spinner" aria-label="Preparing" />
                ) : (
                  <RefreshIcon size={20} />
                )}
              </button>
            </li>
          ))}
        </ol>
      </section>

      {operatorAssignmentPending && (
        <Panel purpose="summary" labelledBy="soundbed-operator-review-heading" className="review__operator">
          <h2 id="soundbed-operator-review-heading">Internal soundbed review</h2>
          <p className="review__operator-help">
            Complete the exact mixed-render review before this soundbed can be
            approved for the story performance. This does not enable family
            soundbeds globally.
          </p>
          <ul className="review__checklist" aria-label="Soundbed review checks" aria-live="polite">
            {operatorChecks.map((check) => (
              <li key={check.id} className={`review__sample review__check${check.complete ? ' complete' : ''}`}>
                <span className="review__check-label">
                  {check.complete ? <CheckIcon size={18} /> : <span className="review__check-slot" aria-hidden="true" />}
                  {check.label}
                </span>
                <small>{check.complete ? 'heard' : check.remaining}</small>
              </li>
            ))}
          </ul>
          <div className="review__confirmations" role="group" aria-label="Operator confirmations">
            <label className="review__confirmation">
              <input
                type="checkbox"
                checked={bookendsForegrounded}
                onChange={(event) => setBookendsForegrounded(event.target.checked)}
              />
              The donor voice, personal introduction, and goodnight remain unmistakably foregrounded.
            </label>
            <label className="review__confirmation">
              <input
                type="checkbox"
                checked={endedInSilence}
                onChange={(event) => setEndedInSilence(event.target.checked)}
              />
              The soundbed ends before or around the personal goodnight, followed by silence.
            </label>
          </div>
          <div className="review__operator-approve">
            <PillButton
              variant="secondary"
              disabled={operatorReviewing || !operatorReviewReady}
              onClick={() => void handleApproveSoundbedAssignment()}
              data-testid="approve-soundbed-assignment-button"
            >
              {operatorReviewing
                ? 'Approving soundbed…'
                : operatorReviewReady
                ? 'Approve exact soundbed assignment'
                : 'Complete soundbed review'}
            </PillButton>
          </div>
        </Panel>
      )}
    </div>
  );
};

export default RenderReview;
